/**
 * Making, saving and deleting the case's named rules (ADR-0100). The case's watchlists are read
 * again after each; the inbox only after a save, since a rule's name and severity are shown on the
 * alerts it raised (a new rule has none, a deleted one's alerts stay as they are).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { RuleDraft, RuleEditing } from '@/components/monitor/RuleForm'
import { useEntitySearch } from '@/features/entity-picker/useEntitySearch'
import { caseMonitorApi } from '@/services/api/case-monitor'
import { toNamedRuleWrite } from './rule-rows'
import { monitorKeys } from './useMonitor'

export type RuleChange =
  | { action: 'submit'; draft: RuleDraft; ruleId: string | null }
  | { action: 'delete'; ruleId: string }

function send(caseId: string, change: RuleChange): Promise<unknown> {
  if (change.action === 'delete') return caseMonitorApi.deleteRule(caseId, change.ruleId)
  const body = toNamedRuleWrite(change.draft)
  return change.ruleId
    ? caseMonitorApi.saveRule(caseId, change.ruleId, body)
    : caseMonitorApi.createRule(caseId, body)
}

/** The rules pane's callbacks for a reader who may change the rules. */
export function useRuleEditing(caseId: string): RuleEditing {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const entitySearch = useEntitySearch(caseId, 'monitor-rule')
  const change = useMutation<unknown, unknown, RuleChange>({
    mutationFn: next => send(caseId, next),
    onError: (_error, next) =>
      toast.error(
        t(
          next.action === 'delete'
            ? 'cases.monitor.rules.form.deleteFailed'
            : 'cases.monitor.rules.form.failed'
        )
      ),
    onSettled: (_data, _error, next) => {
      void queryClient.invalidateQueries({ queryKey: monitorKeys.watchlists(caseId) })
      if (next.action === 'submit' && next.ruleId) {
        void queryClient.invalidateQueries({ queryKey: monitorKeys.alerts(caseId) })
      }
    },
  })
  return {
    onSubmit: async (draft, ruleId) => {
      await change.mutateAsync({ action: 'submit', draft, ruleId })
    },
    onDelete: ruleId => change.mutate({ action: 'delete', ruleId }),
    pending: change.isPending,
    entitySearch,
  }
}
